import { useEffect, useState } from 'react';
import { SITES, WHERE_TO_GO, staffSiteUrl, siteHostLabel, siteByKey } from '../lib/sites';
import useStaffHub from '../hooks/useStaffHub';

// Staff start page (staff.silverservice.com): every site, what it's for and
// who uses it — and where staff sign in (hooks/useStaffHub.jsx). The
// directory itself holds no data and shows to anyone.
export default function StaffHome() {
  const hub = useStaffHub();
  useEffect(() => {
    const previous = document.title;
    document.title = 'Silver Service sites';
    return () => { document.title = previous; };
  }, []);

  if (hub.screen) return hub.screen;
  return (
    <div style={s.page}>
      <style>{CARD_CSS}</style>
      <div style={s.container}>
        <header style={s.header}>
          <img src="/logo-full.png" alt="Silver Service" style={s.logo} />
        </header>

        {hub.panel}
        <div style={s.grid}>
          {SITES.map(site => <SiteCard key={site.key} site={site} />)}
        </div>

        <section style={s.section}>
          <h2 style={s.h2}>Where do I go for…?</h2>
          <div style={s.table}>
            {WHERE_TO_GO.map(({ task, site: key, where }, i) => {
              const site = siteByKey(key);
              return (
                <a key={task} href={staffSiteUrl(site)} style={i ? s.row : { ...s.row, borderTop: 'none' }}>
                  <span style={s.task}>{task}</span>
                  <span style={s.dest}>
                    <span style={s.destSite}>{site.name}</span>
                    {where && <span style={s.destWhere}>{where}</span>}
                  </span>
                </a>
              );
            })}
          </div>
        </section>

        <p style={s.footer}>Can't sign in? Ask Olaf or Alet to send you a new setup link.</p>
      </div>
    </div>
  );
}

// What a site does is one hover away rather than always on show: a mouse
// unfolds it under the card (keyboard focus too), a touch screen taps
// "What's in it". Unfolding over the next row keeps the grid from jumping.
function SiteCard({ site }) {
  const [open, setOpen] = useState(false);
  const soon = !!site.comingSoon;
  const detailsId = `sh-does-${site.key}`;
  return (
    <div className={`sh-card${soon ? ' sh-soon' : ''}${open ? ' sh-open' : ''}`}>
      <div style={s.cardTop}>
        <h3 style={s.cardName}>{site.name}</h3>
        {soon && <span style={s.badge}>Coming soon</span>}
      </div>
      <div style={s.host}>{siteHostLabel(site)}</div>
      <p style={s.tagline}>{site.tagline}</p>
      <div style={s.cardBottom}>
        <div style={s.metaRow}>
          <span style={s.forWho}>For: {site.for}</span>
          <button type="button" className="sh-more" aria-expanded={open} aria-controls={detailsId} onClick={() => setOpen(o => !o)}>
            What's in it
            <svg className="sh-chev" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M2 3.5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </div>
        {soon
          ? <span style={s.soonNote}>{site.comingSoon}</span>
          : <a href={staffSiteUrl(site)} style={s.open}>Open {site.name}</a>}
      </div>
      <ul id={detailsId} className="sh-does">
        {site.does.map(d => <li key={d}>{d}</li>)}
      </ul>
    </div>
  );
}

// Hover lives in CSS (inline styles can't do :hover or media queries).
const CARD_CSS = `
.sh-card { position: relative; background: #fff; border: 1px solid #e3e3e3; border-radius: 12px; padding: 20px;
  display: flex; flex-direction: column; min-width: 0;
  transition: box-shadow .15s ease, border-color .15s ease, border-radius 0s linear .12s; }
.sh-card.sh-soon { background: #fafafa; border-style: dashed; }
.sh-more { display: inline-flex; align-items: center; gap: 5px; background: none; border: none; padding: 0;
  font: inherit; font-size: 0.8rem; font-weight: 600; color: #A50B18; cursor: pointer; white-space: nowrap; }
.sh-chev { transition: transform .15s ease; }
.sh-does { list-style: disc; margin: 0; color: #555; font-size: 0.88rem; line-height: 1.45; }
.sh-does li { margin-bottom: 3px; }

@media (hover: hover) {
  .sh-more { cursor: default; }
  .sh-does { position: absolute; top: 100%; left: -1px; right: -1px; z-index: 5; box-sizing: border-box;
    padding: 12px 20px 16px 38px; background: #fff; border: 1px solid #d6d6d6; border-top: 1px solid #eee;
    border-radius: 0 0 12px 12px; box-shadow: 0 16px 24px -10px rgba(0,0,0,.18);
    opacity: 0; visibility: hidden; transform: translateY(-4px);
    transition: opacity .12s ease, transform .12s ease, visibility 0s linear .12s; }
  .sh-card:hover, .sh-card:focus-within, .sh-card.sh-open {
    z-index: 6; border-color: #d6d6d6; border-bottom-color: transparent; border-radius: 12px 12px 0 0;
    box-shadow: 0 6px 18px rgba(0,0,0,.08);
    transition: box-shadow .15s ease, border-color .15s ease, border-radius 0s linear .1s; }
  .sh-card:hover .sh-does, .sh-card:focus-within .sh-does, .sh-card.sh-open .sh-does {
    opacity: 1; visibility: visible; transform: none;
    transition: opacity .15s ease .1s, transform .15s ease .1s, visibility 0s linear .1s; }
  .sh-card:hover .sh-chev, .sh-card:focus-within .sh-chev, .sh-card.sh-open .sh-chev { transform: rotate(180deg); }
}
@media (hover: none) {
  .sh-does { display: none; padding: 14px 0 0 18px; }
  .sh-card.sh-open .sh-does { display: block; }
  .sh-card.sh-open .sh-chev { transform: rotate(180deg); }
}
@media (prefers-reduced-motion: reduce) {
  .sh-card, .sh-does, .sh-chev { transition: none !important; }
}
`;

const RED = '#A50B18';
const s = {
  page: { minHeight: '100vh', background: '#f5f5f5', padding: '40px 16px 56px', boxSizing: 'border-box' },
  container: { maxWidth: 1100, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'center', marginBottom: 36 },
  logo: { height: 'clamp(88px, 14vw, 132px)', display: 'block' },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))', gap: 16 },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  cardName: { fontSize: '1.15rem', fontWeight: 700, color: '#222', margin: 0 },
  badge: { fontSize: '0.7rem', fontWeight: 600, color: '#8a5a00', background: '#fff4dc', border: '1px solid #f0d9a6', borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' },
  host: { color: '#888', fontSize: '0.8rem', marginTop: 2, overflowWrap: 'anywhere' },
  tagline: { color: '#333', fontSize: '0.95rem', fontWeight: 600, margin: '12px 0 16px' },
  cardBottom: { marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 10 },
  metaRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 },
  forWho: { color: '#777', fontSize: '0.8rem' },
  open: { display: 'block', textAlign: 'center', background: RED, color: '#fff', textDecoration: 'none', borderRadius: 8, padding: '10px 14px', fontWeight: 600, fontSize: '0.9rem' },
  soonNote: { color: '#666', fontSize: '0.82rem', background: '#f0f0f0', borderRadius: 8, padding: '8px 10px' },

  section: { marginTop: 36 },
  h2: { fontSize: '1.2rem', fontWeight: 700, color: '#222', margin: '0 0 12px' },
  table: { background: '#fff', border: '1px solid #e3e3e3', borderRadius: 12, overflow: 'hidden' },
  row: { display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: '4px 16px', padding: '12px 16px', borderTop: '1px solid #f0f0f0', textDecoration: 'none', color: 'inherit' },
  task: { color: '#222', fontSize: '0.93rem', flex: '1 1 260px' },
  dest: { display: 'flex', flexWrap: 'wrap', gap: '2px 8px', alignItems: 'baseline', justifyContent: 'flex-end' },
  destSite: { color: RED, fontWeight: 600, fontSize: '0.9rem' },
  destWhere: { color: '#888', fontSize: '0.82rem' },

  footer: { color: '#888', fontSize: '0.85rem', marginTop: 28, textAlign: 'center' },
};
